import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faPaperPlane,
  faTimes,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import SpinningGruveLogo from '../UI/SpinningGruveLogo';

const iconMapping = {
  'envelope': faEnvelope,
  'paper-plane': faPaperPlane,
  'times': faTimes,
  'spinner': faSpinner
};

const FormRenderer = ({ definition, agentId, onClose, token, onSubmit, loading: externalLoading, workflowContext }) => {
  // Check if this is bank workflow (workflowId 16) - handle both string and number
  const isBankWorkflow = parseInt(workflowContext?.workflowId) === 16;

  console.log('📋 FormRenderer props:', {
    externalLoading,
    workflowId: workflowContext?.workflowId,
    isBankWorkflow,
    shouldShowOverlay: externalLoading && isBankWorkflow
  });

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Bank workflow specific: Progress message cycling
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);

  const bankProcessingMessages = [
    '📄 Extracting transactions...',
    '🔍 Analyzing transaction data...',
    '📊 Categorizing transactions...',
    '🏢 Mapping customers and vendors...',
    '✨ Preparing results...'
  ];

  // Only for Bank Workflow (workflowId === 16): Cycle through processing messages
  useEffect(() => {
    if (externalLoading && isBankWorkflow) {
      const interval = setInterval(() => {
        setProcessingMessageIndex(prev => (prev + 1) % bankProcessingMessages.length);
      }, 3000); // Change message every 3 seconds

      return () => clearInterval(interval);
    } else {
      setProcessingMessageIndex(0); // Reset when not loading
    }
  }, [externalLoading, isBankWorkflow]);

  const handleInputChange = (name, value) => {
    console.log('📝 FormRenderer handleInputChange:', { name, value });
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      console.log('📊 Updated form data:', newData);
      return newData;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    definition.sections?.forEach(section => {
      section.fields?.forEach(field => {
        const fieldValue = formData[field.name];

        // Required field validation
        if (field.required) {
          if (field.type === 'file') {
            // For file fields, check if a file was selected
            if (!fieldValue || !(fieldValue instanceof File)) {
              newErrors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'checkbox') {
            // For checkbox fields, check if checked
            if (!fieldValue) {
              newErrors[field.name] = `${field.label} is required`;
            }
          } else {
            // For text fields, check if not empty after trimming
            if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
              newErrors[field.name] = `${field.label} is required`;
            }
          }
        }

        // Email validation
        if (field.type === 'email' && fieldValue && typeof fieldValue === 'string') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(fieldValue)) {
            newErrors[field.name] = 'Please enter a valid email address';
          }
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAction = async (action, actionConfig) => {
    console.log('🔄 FormRenderer handleAction called:', { action, actionConfig, onSubmit: !!onSubmit, formData });

    // Handle close actions
    if (action === 'close_form' || actionConfig?.type === 'close') {
      onClose();
      return;
    }

    // Handle workflow submit actions (for UI→AI workflows)
    if (action === 'submit' && onSubmit) {
      console.log('📝 Validating form...');
      if (!validateForm()) {
        console.log('❌ Form validation failed');
        return;
      }

      console.log('✅ Form valid, calling onSubmit with data:', formData);
      // Call the workflow submit handler
      onSubmit(formData);
      return;
    }

    // FALLBACK: Handle submit actions with backend_call even without onSubmit (for bill processing)
    if (action === 'submit' && actionConfig?.type === 'backend_call') {
      console.log('🔧 FALLBACK: Handling backend_call submit action directly');
      if (!validateForm()) {
        console.log('❌ Form validation failed');
        return;
      }

      setLoading(true);
      try {
        // Submit directly to workflow continue endpoint
        const response = await fetch('/api/v2/workflows/continue', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            form_data: formData,
            action: actionConfig.function
          })
        });

        if (response.ok) {
          alert(actionConfig.success_message || 'Form submitted successfully!');
          onClose();
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || actionConfig.error_message || 'Submission failed');
        }
      } catch (error) {
        console.error('❌ Fallback submission failed:', error);
        alert(error.message || 'Submission failed');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle any action with an endpoint (traditional forms)
    if (actionConfig?.endpoint) {
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      try {
        const endpoint = actionConfig.endpoint?.replace('{agent_id}', agentId);
        const response = await fetch(endpoint, {
          method: actionConfig.method || 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          alert(actionConfig.success_message || 'Form submitted successfully!');
          onClose();
        } else {
          throw new Error(actionConfig.error_message || 'Submission failed');
        }
      } catch (error) {
        alert(error.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // EMERGENCY FIX: Handle submit + backend_call directly
    if (action === 'submit' && actionConfig?.type === 'backend_call') {
      console.log('🚨 EMERGENCY FIX: Processing submit + backend_call');
      if (!validateForm()) {
        return;
      }

      setLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      fetch('/api/v2/workflows/14/continue', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData
      }).then(response => {
        if (response.ok) {
          alert(actionConfig.success_message || 'Success!');
          onClose();
        } else {
          alert('Submission failed');
        }
      }).catch(error => {
        alert('Error: ' + error.message);
      }).finally(() => {
        setLoading(false);
      });
      return;
    }

    // Unknown action
    console.log('Unknown action:', action, 'Config:', actionConfig);
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        errors[field.name] ? 'border-red-500' : ''
      }`,
      placeholder: field.placeholder,
      value: formData[field.name] || '',
      onChange: (e) => handleInputChange(field.name, e.target.value)
    };

    switch (field.type) {
      case 'text':
      case 'email':
        return <input type={field.type} {...commonProps} />;

      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={field.rows || 3}
          />
        );

      case 'select':
        return (
          <select
            {...commonProps}
            value={formData[field.name] || ''}
            onChange={(e) => {
              const selectedValue = e.target.value;
              handleInputChange(field.name, selectedValue);

              // For bank field, also capture the bank name (label)
              if (field.name === 'bank' && selectedValue && field.options) {
                const selectedOption = field.options.find(opt => opt.value === selectedValue);
                if (selectedOption && selectedOption.label) {
                  handleInputChange('bank_name', selectedOption.label);
                }
              }

              // ✅ SAFE: Only auto-populate if field has data_mapping config
              // This won't affect bill processing which doesn't use data_mapping
              if (selectedValue && field.data_mapping && field.options) {
                const selectedOption = field.options.find(opt => opt.value === selectedValue);
                if (selectedOption) {
                  // Auto-populate fields based on data_mapping configuration
                  Object.entries(field.data_mapping).forEach(([targetField, sourceKey]) => {
                    if (selectedOption[sourceKey]) {
                      handleInputChange(targetField, selectedOption[sourceKey]);
                    }
                  });
                }
              }
            }}
          >
            <option value="">Select an option</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <input
            type="checkbox"
            id={field.name}
            name={field.name}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            checked={formData[field.name] || false}
            onChange={(e) => handleInputChange(field.name, e.target.checked)}
          />
        );

      case 'file':
        return (
          <div className={`professional-upload-container ${field.className || ''}`}>
            <div className="upload-area">
              <input
                type="file"
                id={field.name}
                name={field.name}
                className="upload-input"
                accept={field.accept || '*'}
                disabled={externalLoading || loading}
                onChange={(e) => {
                  const file = e.target.files[0];
                  console.log('📎 File selected:', file);
                  if (file) {
                    // Check file size if specified
                    if (field.max_size) {
                      const maxBytes = parseMaxSize(field.max_size);
                      if (file.size > maxBytes) {
                        alert(`File size must be less than ${field.max_size}`);
                        e.target.value = '';
                        return;
                      }
                    }
                    console.log('✅ File validated, updating form data');
                    handleInputChange(field.name, file);
                  }
                }}
              />
              <label htmlFor={field.name} className={`upload-label ${(externalLoading || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <div className="upload-icon">📄</div>
                <div className="upload-text">
                  {formData[field.name] ? (
                    <div className="file-selected-container">
                      <span className="file-selected">{formData[field.name].name}</span>
                      {!(externalLoading || loading) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Clear the file from form data
                            handleInputChange(field.name, null);
                            // Reset the file input
                            const fileInput = document.getElementById(field.name);
                            if (fileInput) fileInput.value = '';
                          }}
                          className="ml-2 text-red-500 hover:text-red-700 text-sm font-medium"
                          title="Remove file"
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <span className="upload-main">Click to upload or drag and drop</span>
                      <span className="upload-sub">{field.helpText || 'Select your file'}</span>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>
        );

      default:
        return <input type="text" {...commonProps} />;
    }
  };

  const renderContent = (content, index = 0) => {
    switch (content.type) {
      case 'text':
        const Tag = content.tag || 'p';
        return (
          <Tag
            key={`text-${index}`}
            className={content.css_class || ''}
            style={content.style ? parseStyle(content.style) : {}}
          >
            {content.content}
          </Tag>
        );

      case 'icon':
        return (
          <div
            key={`icon-${index}`}
            className={content.css_class || ''}
            style={content.style ? parseStyle(content.style) : {}}
          >
            <FontAwesomeIcon
              icon={iconMapping[content.icon] || faEnvelope}
            />
          </div>
        );

      case 'button':
        const actionConfig = definition.actions?.[content.action];
        return (
          <button
            key={`button-${index}`}
            className={content.css_class || 'btn'}
            style={content.style ? parseStyle(content.style) : {}}
            onClick={() => handleAction(content.action, actionConfig)}
            disabled={loading}
          >
            {loading && content.action === 'submit_form' ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            ) : content.icon ? (
              <FontAwesomeIcon
                icon={iconMapping[content.icon] || faEnvelope}
                className="mr-2"
              />
            ) : null}
            {content.text}
          </button>
        );

      default:
        return null;
    }
  };

  const renderSection = (section, sectionIndex = 0) => {
    if (section.type === 'field_group') {
      return (
        <div key={`section-${sectionIndex}`} className={section.css_class || ''}>
          {section.title && (
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {section.title}
            </h3>
          )}
          
          {section.fields?.map((field, index) => (
            <div key={`field-${field.name}`} className={field.css_class || 'mb-4'}>
              {/* Only show label for non-file fields */}
              {field.type !== 'file' && (
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}

              {field.type === 'checkbox' ? (
                <div className="flex items-center">
                  {renderField(field)}
                  <label
                    htmlFor={field.name}
                    className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                  >
                    {field.label}
                  </label>
                </div>
              ) : (
                renderField(field)
              )}

              {errors[field.name] && (
                <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div key={`section-${sectionIndex}`} className={section.css_class || ''}>
        {section.content?.map((content, index) => (
          <div key={`content-${index}`}>
            {renderContent(content, index)}
          </div>
        ))}
      </div>
    );
  };

  const parseStyle = (styleStr) => {
    if (!styleStr) return {};
    const styles = {};
    styleStr.split(';').forEach(rule => {
      const [property, value] = rule.split(':').map(s => s.trim());
      if (property && value) {
        const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styles[camelCaseProperty] = value;
      }
    });
    return styles;
  };

  const parseMaxSize = (sizeStr) => {
    if (!sizeStr) return Infinity;
    const unit = sizeStr.toLowerCase().slice(-2);
    const value = parseInt(sizeStr);

    switch (unit) {
      case 'kb': return value * 1024;
      case 'mb': return value * 1024 * 1024;
      case 'gb': return value * 1024 * 1024 * 1024;
      default: return value;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto relative"
        style={{
          width: definition.config?.width || '600px',
          ...(definition.config?.custom_css ? parseStyle(definition.config.custom_css) : {})
        }}
      >
        {/* Close button */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            data-close-form="true"
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Header */}
        {definition.header && (
          <div className={definition.header.css_class || ''}>
            {definition.header.sections?.map((section, index) => (
              <div key={`header-${index}`}>
                {renderContent(section, index)}
              </div>
            ))}
          </div>
        )}

        {/* Form Sections */}
        <div className="p-6">
          {definition.sections?.map((section, index) => (
            <div key={`form-section-${index}`}>
              {renderSection(section, index)}
            </div>
          ))}

          {/* Submit and Cancel Buttons for Workflow Forms */}
          {definition.actions?.submit && (
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => handleAction('submit', definition.actions.submit)}
                disabled={externalLoading || loading}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: '600',
                  padding: '0.875rem 2rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: externalLoading || loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem',
                  boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
                  opacity: externalLoading || loading ? 0.6 : 1,
                  flex: '1'
                }}
              >
                {(externalLoading || loading) && (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                )}
                {(externalLoading || loading)
                  ? (definition.actions.submit.loading_message || 'Processing...')
                  : (definition.actions.submit.button_text || 'Submit')
                }
              </button>

              {/* Cancel button - closes form via onClose callback */}
              <button
                type="button"
                onClick={() => {
                  console.log('🚫 Cancel button clicked - calling onClose');
                  if (onClose) {
                    onClose();
                  }
                }}
                disabled={externalLoading || loading}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  fontWeight: '600',
                  padding: '0.875rem 2rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: externalLoading || loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem',
                  opacity: externalLoading || loading ? 0.5 : 1,
                  flex: '1'
                }}
                onMouseEnter={(e) => !externalLoading && !loading && (e.target.style.background = '#4b5563')}
                onMouseLeave={(e) => !externalLoading && !loading && (e.target.style.background = '#6b7280')}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {definition.footer && (
          <div className={definition.footer.css_class || ''}>
            {definition.footer.content?.map((content, index) => (
              <div key={`footer-${index}`}>
                {renderContent(content, index)}
              </div>
            ))}
          </div>
        )}

        {/* Bank Workflow Only: Processing Overlay with Progress Messages */}
        {externalLoading && isBankWorkflow && (
          <div
            className="absolute inset-0 bg-white bg-opacity-95 dark:bg-gray-900 dark:bg-opacity-95 flex flex-col items-center justify-center rounded-lg z-50"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            <div className="flex flex-col items-center space-y-6 p-8">
              {/* Spinning Logo */}
              <SpinningGruveLogo
                size="w-20 h-20"
                className="text-purple-600 dark:text-purple-400"
                showBackground={true}
              />

              {/* Progress Message */}
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Processing Your Bank Statement
                </h3>
                <p className="text-lg text-purple-600 dark:text-purple-400 font-medium animate-pulse">
                  {bankProcessingMessages[processingMessageIndex]}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  This may take a few moments. Please don't close this window.
                </p>
              </div>

              {/* Progress Indicator */}
              <div className="w-64 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full animate-pulse"
                  style={{
                    width: `${((processingMessageIndex + 1) / bankProcessingMessages.length) * 100}%`,
                    transition: 'width 3s ease-in-out'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormRenderer;